// src/pages/configuracion/components/CambiarPasswordModal.tsx
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  Input,
} from '@/components/ui';
import { toast } from '@/components/ui/Toast';
import { Lock, Eye, EyeOff } from 'lucide-react';
import { useCambiarPassword } from '@/hooks/queries/useConfiguracion';
import { useState } from 'react';

const schema = z.object({
  password_actual: z.string().min(1, 'La contraseña actual es requerida'),
  password_nuevo: z.string()
    .min(8, 'La contraseña debe tener al menos 8 caracteres')
    .regex(/[A-Z]/, 'Debe contener al menos una mayúscula')
    .regex(/[a-z]/, 'Debe contener al menos una minúscula')
    .regex(/[0-9]/, 'Debe contener al menos un número'),
  confirmar_password: z.string().min(1, 'Confirma la nueva contraseña'),
}).refine((data) => data.password_nuevo === data.confirmar_password, {
  message: 'Las contraseñas no coinciden',
  path: ['confirmar_password'],
});

type FormData = z.infer<typeof schema>;

interface CambiarPasswordModalProps {
  open: boolean;
  onClose: () => void;
}

export function CambiarPasswordModal({ open, onClose }: CambiarPasswordModalProps) {
  const cambiarMutation = useCambiarPassword();
  const [showPasswords, setShowPasswords] = useState({
    actual: false,
    nuevo: false,
    confirmar: false,
  });

  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      password_actual: '',
      password_nuevo: '',
      confirmar_password: '',
    },
  });

  const onSubmit = async (data: FormData) => {
    try {
      await cambiarMutation.mutateAsync(data);
      toast.success('Contraseña actualizada correctamente');
      form.reset();
      onClose();
    } catch {
      // Error handled by global MutationCache.onError
    }
  };

  const toggleShow = (field: keyof typeof showPasswords) => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Cambiar Contraseña</DialogTitle>
        </DialogHeader>

        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <div className="relative">
            <Input
              label="Contraseña actual"
              type={showPasswords.actual ? 'text' : 'password'}
              {...form.register('password_actual')}
              error={form.formState.errors.password_actual?.message}
            />
            <button
              type="button"
              onClick={() => toggleShow('actual')}
              className="absolute right-3 top-8 text-gray-400 hover:text-gray-600"
            >
              {showPasswords.actual ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>

          <div className="relative">
            <Input
              label="Nueva contraseña"
              type={showPasswords.nuevo ? 'text' : 'password'}
              {...form.register('password_nuevo')}
              error={form.formState.errors.password_nuevo?.message}
            />
            <button
              type="button"
              onClick={() => toggleShow('nuevo')}
              className="absolute right-3 top-8 text-gray-400 hover:text-gray-600"
            >
              {showPasswords.nuevo ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>

          <div className="relative">
            <Input
              label="Confirmar nueva contraseña"
              type={showPasswords.confirmar ? 'text' : 'password'}
              {...form.register('confirmar_password')}
              error={form.formState.errors.confirmar_password?.message}
            />
            <button
              type="button"
              onClick={() => toggleShow('confirmar')}
              className="absolute right-3 top-8 text-gray-400 hover:text-gray-600"
            >
              {showPasswords.confirmar ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>

          <div className="text-sm text-gray-500 bg-gray-50 p-3 rounded-md">
            <p className="font-medium mb-1">Requisitos de la contraseña:</p>
            <ul className="list-disc list-inside space-y-0.5">
              <li>Mínimo 8 caracteres</li>
              <li>Al menos una letra mayúscula</li>
              <li>Al menos una letra minúscula</li>
              <li>Al menos un número</li>
            </ul>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" loading={cambiarMutation.isPending}>
              <Lock className="h-4 w-4 mr-2" />
              Cambiar Contraseña
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
